import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiCheck, FiFilm, FiHeart, FiMaximize2, FiPause, FiPlay } from 'react-icons/fi';
import xtreamAPI, { XtreamContent, XtreamSeriesInfo, XtreamEpisode } from '../../api/xtreamAPI';
import {
  getRuntimeTmdbLanguage,
  fetchTvSeason,
  TmdbTvDetailsResult,
  TmdbSeriesResult,
  TmdbSeasonEpisode,
} from '../../api/tmdbAPI';
import { getCatalogRecommendations } from '../../api/backendAPI';
import { useDevice } from '../../contexts/DeviceContext';
import { consumeCatalogForceRefresh } from '../../utils/catalogRefresh';
import { getCachedSeriesInfo } from '../../utils/seriesInfoCache';
import { getContentDataSource, ContentDataSource } from '../../utils/contentDataSource';
import { useWatchProgress, formatProgressTime, ProgressEntry } from '../../contexts/WatchProgressContext';
import { useWatchHistory } from '../../contexts/WatchHistoryContext';
import {
  PlaybackOverlayStatus,
  PlaybackStatusOverlay,
} from '../../components/PlaybackStatusOverlay/PlaybackStatusOverlay';
import { VideoControlsOverlay, AudioTrackInfo, SubtitleTrackInfo } from '../../components/VideoControlsOverlay/VideoControlsOverlay';
import { SamsungAvPlayVod, shouldUseSamsungAvPlayVod } from '../../utils/samsungAvPlayVod';

import { TrailerModal } from '../../components/TrailerModal/TrailerModal';
import { AlternativesModal } from '../../components/AlternativesModal/AlternativesModal';
import { resolveContentIdentity } from '../../utils/contentIdentityResolver';
import './SeriesDetails.css';

/** Chrome 47 compat: play() returns undefined instead of a Promise */
function safePlay(video: HTMLVideoElement): void {
  const p = video.play();
  if (p !== undefined) p.catch(() => {});
}

// ─── Entry type (also exported for App.tsx / ContentList.tsx) ─────────────────

export interface SeriesDetailsEntry {
  /** TMDB series id */
  tmdbId: number;
  /** Pre-loaded title (shown while details load) */
  title?: string;
  /** Pre-loaded poster URL (used as favorite icon) */
  posterUrl?: string;
  /** Pre-loaded backdrop URL */
  backdropUrl?: string;
  /** Xtream series_id — enables episode listing */
  seriesStreamId?: string;
  /** Xtream category_id used by parental filtering and favorites. */
  categoryId?: string;
  /** When true, this series was opened from a kids context (Kids page or kids-series source). */
  isKids?: boolean;
  /** Sinopse da listagem Xtream — usada para desambiguar busca TMDB */
  plot?: string;
  /** Ano de estreia da listagem Xtream (ex: "2022") */
  year?: string;
  /** Data de estreia da listagem Xtream (ex: "2022-10-21") */
  releaseDate?: string;
  /** Data de estreia da listagem Xtream — variante snake_case */
  release_date?: string;
}

// ─── Watch progress types ─────────────────────────────────────────────────────

export interface EpisodeProgress {
  percent: number; // 0–100
}

export interface SeriesWatchProgress {
  /** Keyed by XtreamEpisode.id */
  episodes: Record<string, EpisodeProgress>;
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface SeriesDetailsProps {
  entry: SeriesDetailsEntry;
  isFavorite?: boolean;
  isActive?: boolean;
  watchProgress?: SeriesWatchProgress;
  onPlayEpisode?: (episodeStreamId: string, ext?: string) => void;
  onToggleFavorite?: (tmdbId: number) => void;
  onBack?: () => void;
  onSelectRecommended?: (entry: SeriesDetailsEntry) => void;
  onIdentityResolved?: (tmdbId: number) => void;
}

// ─── Internal types ───────────────────────────────────────────────────────────

interface RecItem {
  tmdb: TmdbSeriesResult;
  seriesStreamId?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso: string | undefined): string {
  if (!iso) return '';
  try {
    const [y, m, d] = iso.split('-');
    return `${d}/${m}/${y}`;
  } catch {
    return iso;
  }
}

function formatDuration(raw?: string): string {
  if (!raw) return '';
  const n = parseInt(raw, 10);
  if (!n) return raw;
  const h = Math.floor(n / 60);
  const m = n % 60;
  return h ? `${h}h${m > 0 ? ` ${m}m` : ''}` : `${m}m`;
}

function getSeasonStatus(
  eps: XtreamEpisode[],
  progress?: Record<string, EpisodeProgress>,
): 'completed' | 'watching' | 'notStarted' {
  if (!progress || eps.length === 0) return 'notStarted';
  const percents = eps.map((ep) => progress[ep.id]?.percent ?? 0);
  const watched = percents.filter((p) => p >= 90).length;
  const started = percents.filter((p) => p > 3).length;
  if (watched === eps.length) return 'completed';
  if (started > 0) return 'watching';
  return 'notStarted';
}

/** Index of the "current" episode: last in-progress, or first unwatched, or 0 */
function getCurrentEpisodeIdx(
  eps: XtreamEpisode[],
  getProgress: (id: string, streamType: 'series_episode') => ProgressEntry | null,
): number {
  if (eps.length === 0) return 0;
  // Find the episode most recently watched (highest updatedAt)
  let bestIdx = -1;
  let bestUpdatedAt = -1;
  for (let i = 0; i < eps.length; i++) {
    const p = getProgress(eps[i].id, 'series_episode');
    if (!p) continue;
    const pct = p.duration > 0 ? (p.position / p.duration) * 100 : 0;
    if (pct < 3) continue; // never really watched
    if ((p.updatedAt ?? 0) > bestUpdatedAt) {
      bestUpdatedAt = p.updatedAt ?? 0;
      bestIdx = i;
    }
  }
  if (bestIdx === -1) return 0;
  const p = getProgress(eps[bestIdx].id, 'series_episode')!;
  const pct = p.duration > 0 ? (p.position / p.duration) * 100 : 0;
  if (pct >= 90 && bestIdx + 1 < eps.length) return bestIdx + 1;
  return bestIdx;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

interface RecCardProps {
  series?: TmdbSeriesResult;
  loading?: boolean;
  onSelect: () => void;
  focused: boolean;
}

const RecCard: React.FC<RecCardProps> = ({ series, loading, onSelect, focused }) => {
  const style: React.CSSProperties = series?.posterUrl
    ? { backgroundImage: `url(${series.posterUrl})` }
    : {};
  return (
    <div
      className={[
        'series-details__rec-card',
        loading ? 'series-details__rec-card--loading' : '',
        focused ? 'series-details__rec-card--focused' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={style}
      onClick={onSelect}
      tabIndex={-1}
    >
      {series?.title && !loading && (
        <div className="series-details__rec-card-title">{series.title}</div>
      )}
    </div>
  );
};

// ─── Season chip ──────────────────────────────────────────────────────────────

interface SeasonChipProps {
  seasonKey: string;
  status: 'completed' | 'watching' | 'notStarted';
  active: boolean;
  focused: boolean;
  onClick: () => void;
}

const SeasonChip: React.FC<SeasonChipProps> = ({ seasonKey, status, active, focused, onClick }) => {
  const { t } = useTranslation();
  const num = String(parseInt(seasonKey, 10)).padStart(2, '0');
  const StatusIcon =
    status === 'completed' ? FiCheck
    : status === 'watching' ? FiPause
    : null;
  return (
    <button
      className={[
        'series-details__season-chip',
        active  ? 'series-details__season-chip--active'  : '',
        focused ? 'series-details__season-chip--focused' : '',
      ].filter(Boolean).join(' ')}
      onClick={onClick}
      tabIndex={-1}
    >
      {t('seriesDetails.seasonPrefix')}{num}
      {StatusIcon && (
        <span className={`series-details__season-badge series-details__season-badge--${status}`}>
          <StatusIcon size={9} />
        </span>
      )}
    </button>
  );
};

// ─── Episode row (vertical list) ─────────────────────────────────────────────

interface EpisodeRowProps {
  episode: XtreamEpisode;
  tmdbEp?: TmdbSeasonEpisode;
  seasonKey: string;
  progress?: EpisodeProgress;
  numDigits: number;
  focused: boolean;
  current: boolean;
  onClick: () => void;
}

const EpisodeRow: React.FC<EpisodeRowProps> = ({
  episode,
  tmdbEp,
  seasonKey: _seasonKey,
  progress,
  numDigits,
  focused,
  current,
  onClick,
}) => {
  const { t } = useTranslation();
  const pct = progress?.percent ?? 0;
  const watched = pct >= 90;
  const plot = tmdbEp?.overview || episode.info?.plot || episode.plot || '';
  const title = tmdbEp?.title || episode.title || '';
  const dur = tmdbEp?.runtime != null
    ? formatDuration(String(tmdbEp.runtime))
    : formatDuration(episode.info?.duration ?? episode.duration);
  const epNum = String(episode.episode_num).padStart(numDigits, '0');
  return (
    <div
      className={[
        'series-details__ep-row',
        focused  ? 'series-details__ep-row--focused'  : '',
        current  ? 'series-details__ep-row--current'  : '',
        watched  ? 'series-details__ep-row--watched'  : '',
      ].filter(Boolean).join(' ')}
      onClick={onClick}
      tabIndex={-1}
    >
      <span className="series-details__ep-num">{t('seriesDetails.episodePrefix')}{epNum}</span>
      <div className="series-details__ep-body">
        <div className="series-details__ep-title-line">
          <span className="series-details__ep-title">{title || t('seriesDetails.episodeFallback', { num: episode.episode_num })}</span>
          {dur && <span className="series-details__ep-dur">{dur}</span>}
        </div>
        {pct > 0 && (
          <div className="series-details__ep-progress-wrap">
            <div className="series-details__ep-bar-track">
              <div
                className={`series-details__ep-bar-fill${watched ? ' series-details__ep-bar-fill--done' : ''}`}
                style={{ width: `${Math.min(pct, 100)}%` }}
              />
            </div>
            <span className="series-details__ep-pct">{Math.round(pct)}%</span>
          </div>
        )}
        {plot && (
          <span className="series-details__ep-plot">{plot}</span>
        )}
      </div>
      {watched && (
        <span className="series-details__ep-icon series-details__ep-icon--done">
          <FiCheck size={12} />
        </span>
      )}
      {!watched && pct > 3 && (
        <span className="series-details__ep-icon series-details__ep-icon--current">
          <FiPause size={12} />
        </span>
      )}
    </div>
  );
};

// ─── Find next episode in series ──────────────────────────────────────────────

function findNextEpisode(
  epId: string,
  info: XtreamSeriesInfo | null,
  seasonKeys: string[],
): XtreamEpisode | null {
  if (!info) return null;
  for (let si = 0; si < seasonKeys.length; si++) {
    const eps = info.episodes[seasonKeys[si]] ?? [];
    const idx = eps.findIndex((e) => e.id === epId);
    if (idx === -1) continue;
    if (idx + 1 < eps.length) return eps[idx + 1];
    // Last ep of this season → first ep of next season
    if (si + 1 < seasonKeys.length) return info.episodes[seasonKeys[si + 1]]?.[0] ?? null;
    return null; // End of series
  }
  return null;
}

// ─── Main component ───────────────────────────────────────────────────────────

export const SeriesDetails: React.FC<SeriesDetailsProps> = ({
  entry,
  isFavorite = false,
  isActive = true,
  watchProgress,
  onPlayEpisode,
  onToggleFavorite,
  onBack,
  onSelectRecommended,
  onIdentityResolved,
}) => {
  const { t } = useTranslation();
  const { deviceId, activationPassword } = useDevice();
  const [details, setDetails] = useState<TmdbTvDetailsResult | null>(null);
  const [xtreamInfo, setXtreamInfo] = useState<XtreamSeriesInfo | null>(null);
  const [xtreamInfoLoaded, setXtreamInfoLoaded] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(true);
  // tmdbId resolved from entry (may require a /search/tv lookup when entry has no tmdbId)
  const [resolvedTmdbId, setResolvedTmdbId] = useState(entry.tmdbId ?? 0);
  const [similar, setSimilar] = useState<RecItem[]>([]);
  const [loadingSimilar, setLoadingSimilar] = useState(true);
  const [backdropUrl, setBackdropUrl] = useState<string | null>(entry.backdropUrl ?? null);
  const [contentDataSource] = useState<ContentDataSource>(getContentDataSource);
  /** Cache of TMDB season data: key = season number (int) */
  const [tmdbSeasonCache, setTmdbSeasonCache] = useState<Map<number, Map<number, TmdbSeasonEpisode>>>(new Map());
  /** Tracks which season numbers have been requested (prevents duplicate fetches) */
  const tmdbSeasonFetchedRef = useRef<Set<number>>(new Set());

  const [focusSection, setFocusSection] = useState<'buttons' | 'seasons' | 'episodes' | 'recs'>('buttons');
  const [focusedBtnIdx, setFocusedBtnIdx] = useState(0);
  const [focusedSeasonIdx, setFocusedSeasonIdx] = useState(0);
  const [focusedEpisodeIdx, setFocusedEpisodeIdx] = useState(0);
  const [focusedRec, setFocusedRec] = useState(0);

  // ── Next-episode overlay state ────────────────────────────────────────────
  const [showNextEpOverlay, setShowNextEpOverlay] = useState(false);
  const [nextEpForOverlay, setNextEpForOverlay] = useState<XtreamEpisode | null>(null);
  const [nextEpFocusedBtn, setNextEpFocusedBtn] = useState(0); // 0 = Sim, 1 = Não

  const recsRef = useRef<HTMLDivElement>(null);
  const recRowRef = useRef<HTMLDivElement>(null);
  const seasonWrapRef = useRef<HTMLDivElement>(null);  // o wrapper scrollável
  const seasonRowRef = useRef<HTMLDivElement>(null);   // a flex row interna (para children)
  const epListRef = useRef<HTMLDivElement>(null);
  /** Prevent auto-select from running more than once per entry */
  const autoSelectedRef = useRef(false);
  /** Episodes played back-to-back without user manually returning to SeriesDetails */
  const consecutiveEpsRef = useRef(0);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Kept in sync with xtreamInfo state for use in stable closures */
  const xtreamInfoRef = useRef<XtreamSeriesInfo | null>(null);
  const seasonsRef = useRef<string[]>([]);
  /** Stable wrapper for the ended handler — updated via its own useEffect */
  const onEndedRef = useRef<() => void>(() => {});

  // ── Episode player state ──────────────────────────────────────────────────
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [pendingEp, setPendingEp] = useState<XtreamEpisode | null>(null);
  const [pendingSeekPos, setPendingSeekPos] = useState(0);
  const [resumeModalFocusedBtn, setResumeModalFocusedBtn] = useState(1); // 0 = from start, 1 = continue
  const [epVideoStatus, setEpVideoStatus] = useState<PlaybackOverlayStatus | null>(null);
  const [fsEpTitle, setFsEpTitle] = useState<string>('');
  const [showTrailerModal, setShowTrailerModal] = useState(false);
  const [showAlternativesModal, setShowAlternativesModal] = useState(false);

  // ── Audio / subtitle track state (populated when HLS has tracks) ──────────
  const [audioTracks, setAudioTracks] = useState<AudioTrackInfo[]>([]);
  const [activeAudioId, setActiveAudioId] = useState(0);
  const [subtitleTracks, setSubtitleTracks] = useState<SubtitleTrackInfo[]>([]);
  const [activeSubtitleId, setActiveSubtitleId] = useState(-1);

  const videoRef = useRef<HTMLVideoElement>(null);
  const playerWrapRef = useRef<HTMLDivElement>(null);
  const hlsRef = useRef<any>(null);
  const avPlayRef = useRef(new SamsungAvPlayVod());
  const forcedSubtitleAppliedRef = useRef(false);
  const playingEpisodeIdRef = useRef<string | null>(null);
  const pendingSeekRef = useRef(0);
  const lastProgressSaveRef = useRef(0);
  const [isEpisodeFullscreen, setIsEpisodeFullscreen] = useState(false);
  const [isAvPlayActive, setIsAvPlayActive] = useState(false);

  const { getProgress, setProgress, clearProgress, flushProgress } = useWatchProgress();
  const { addToHistory } = useWatchHistory();

  // Derived seasons list
  const seasons = useMemo<string[]>(() => {
    if (!xtreamInfo?.episodes) return [];
    return Object.keys(xtreamInfo.episodes)
      .filter((k) => Array.isArray(xtreamInfo.episodes[k]) && xtreamInfo.episodes[k].length > 0)
      .sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
  }, [xtreamInfo]);

  const selectedSeason = seasons[focusedSeasonIdx] ?? null;

  // ── Fetch TMDB season data lazily when season changes ─────────────────────
  useEffect(() => {
    if (contentDataSource === 'xtream') return;
    if (!selectedSeason || !resolvedTmdbId) return;
    const seasonNum = parseInt(selectedSeason, 10);
    if (isNaN(seasonNum) || seasonNum <= 0) return;
    if (tmdbSeasonFetchedRef.current.has(seasonNum)) return; // already requested
    tmdbSeasonFetchedRef.current.add(seasonNum);
    fetchTvSeason(resolvedTmdbId, seasonNum).then((epMap) => {
      setTmdbSeasonCache((prev) => {
        const next = new Map(prev);
        next.set(seasonNum, epMap);
        return next;
      });
    });
  }, [selectedSeason, resolvedTmdbId]);

  const episodes = useMemo<XtreamEpisode[]>(() => {
    if (!selectedSeason || !xtreamInfo?.episodes) return [];
    return xtreamInfo.episodes[selectedSeason] ?? [];
  }, [selectedSeason, xtreamInfo]);

  // ── Derive watch progress from context ───────────────────────────────────
  const effectiveProgress = useMemo((): SeriesWatchProgress => {
    if (!xtreamInfo?.episodes) return watchProgress ?? { episodes: {} };
    const eps: Record<string, EpisodeProgress> = {};
    for (const epList of Object.values(xtreamInfo.episodes)) {
      for (const ep of epList) {
        const p = getProgress(ep.id, 'series_episode');
        if (p) {
          eps[ep.id] = { percent: (p.position / p.duration) * 100 };
        }
      }
    }
    return Object.keys(eps).length > 0 ? { episodes: eps } : (watchProgress ?? { episodes: {} });
  }, [xtreamInfo, getProgress, watchProgress]);

  // Current episode in the active season — based on most recently watched (updatedAt)
  const currentEpisodeIdx = useMemo(
    () => getCurrentEpisodeIdx(episodes, getProgress),
    [episodes, getProgress],
  );

  // Total episodes across all seasons (for info display)
  const totalEpisodes = useMemo(() => {
    if (!xtreamInfo?.episodes) return 0;
    return Object.values(xtreamInfo.episodes).reduce(
      (sum, eps) => sum + (Array.isArray(eps) ? eps.length : 0), 0,
    );
  }, [xtreamInfo]);

  // ── Episode player: direct-fullscreen video ───────────────────────────────

  const doPlayEpisode = useCallback((ep: XtreamEpisode, seekPos: number) => {
    setShowResumeModal(false);

    const wrapper = playerWrapRef.current;
    const video = videoRef.current;
    if (!wrapper || !video) return;

    wrapper.style.display = 'flex';

    addToHistory({
      id: String(resolvedTmdbId || entry.tmdbId || entry.seriesStreamId || ''),
      type: 'series',
      name: entry.title ?? details?.title ?? '',
      posterUrl: entry.posterUrl ?? details?.posterUrl ?? undefined,
      backdropUrl: entry.backdropUrl ?? backdropUrl ?? undefined,
      streamId: entry.seriesStreamId ?? '',
      lastEpisodeStreamId: ep.id,
      isKids: entry.isKids ?? false,
      updatedAt: Date.now(),
    });

    if (!document.fullscreenElement &&
        !(document as any).webkitFullscreenElement &&
        !(document as any).mozFullScreenElement) {
      const el: any = document.documentElement;
      const p: Promise<void> | undefined =
        el.requestFullscreen?.()
        ?? el.webkitRequestFullscreen?.()
        ?? el.mozRequestFullScreen?.();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    }

    playingEpisodeIdRef.current = ep.id;
    setFsEpTitle(`S${ep.season} E${ep.episode_num} • ${ep.title ?? ''}`.trim());
    pendingSeekRef.current = seekPos;
    lastProgressSaveRef.current = 0;
    setEpVideoStatus('connecting');

    hlsRef.current?.destroy();
    hlsRef.current = null;
    forcedSubtitleAppliedRef.current = false;
    setAudioTracks([]);
    setActiveAudioId(0);
    setSubtitleTracks([]);
    setActiveSubtitleId(-1);
    video.src = '';

    const ext = ep.container_extension || 'mp4';
    const url = ep.direct_source
      ? xtreamAPI.preparePlaybackUrl(ep.direct_source)
      : xtreamAPI.buildStreamUrl(ep.id, 'series', ext);
    const isHls = url.includes('.m3u8');
    const useAvPlay = shouldUseSamsungAvPlayVod(ext, url);

    if (useAvPlay) {
      setIsAvPlayActive(true);
      avPlayRef.current.start(url, seekPos, {
        onStatus: setEpVideoStatus,
        onTracks: (audio, subtitles) => {
          setAudioTracks(audio);
          setActiveAudioId(audio[0]?.id ?? 0);
          setSubtitleTracks(subtitles);
          setActiveSubtitleId(-1);
        },
        onTime: (currentTime, duration) => {
          const epId = playingEpisodeIdRef.current;
          const now = Date.now();
          if (!epId || duration <= 10 || now - lastProgressSaveRef.current < 5000) return;
          lastProgressSaveRef.current = now;
          setProgress(epId, currentTime, duration, 'series_episode');
        },
        onEnded: () => onEndedRef.current(),
      });
      return;
    }
    setIsAvPlayActive(false);

    if (isHls) {
      Promise.resolve().then(() => {
        const Hls: typeof import('hls.js').default = (window as any).Hls;
        if (!videoRef.current || !playingEpisodeIdRef.current) return;
        if (Hls && Hls.isSupported()) {
          const hls = new Hls({ autoStartLoad: true });
          hls.loadSource(url);
          hls.attachMedia(videoRef.current);
          hls.on(Hls.Events.MANIFEST_PARSED, () => {
            if (videoRef.current) safePlay(videoRef.current);
          });
          hls.on(Hls.Events.ERROR, (_e: any, data: any) => {
            if (data.fatal) setEpVideoStatus('error');
          });
          // Track detection
          hls.on(Hls.Events.AUDIO_TRACKS_UPDATED, (_e: any, d: any) => {
            const tracks = (d?.audioTracks ?? []).map((track: any) => ({ id: track.id, name: track.name || track.lang || t('playback.audioTrack', { num: track.id + 1 }), lang: track.lang }));
            setAudioTracks(tracks);
            setActiveAudioId(hls.audioTrack >= 0 ? hls.audioTrack : 0);
          });
          hls.on(Hls.Events.AUDIO_TRACK_SWITCHED, (_e: any, d: any) => {
            setActiveAudioId(typeof d?.id === 'number' ? d.id : hls.audioTrack);
          });
          hls.on(Hls.Events.SUBTITLE_TRACKS_UPDATED, (_e: any, d: any) => {
            const tracks = (d?.subtitleTracks ?? []).map((track: any) => ({ id: track.id, name: track.name || track.lang || t('playback.subtitleTrack', { num: track.id + 1 }), lang: track.lang, forced: !!track.forced }));
            setSubtitleTracks(tracks);
            const forced = forcedSubtitleAppliedRef.current ? undefined : tracks.find((track: SubtitleTrackInfo) => track.forced);
            if (forced) forcedSubtitleAppliedRef.current = true;
            if (forced) hls.subtitleTrack = forced.id;
            setActiveSubtitleId(forced?.id ?? hls.subtitleTrack);
          });
          hls.on(Hls.Events.SUBTITLE_TRACK_SWITCH, (_e: any, d: any) => {
            setActiveSubtitleId(typeof d?.id === 'number' ? d.id : hls.subtitleTrack);
          });
          hlsRef.current = hls;
        } else if (videoRef.current.canPlayType('application/vnd.apple.mpegurl')) {
          videoRef.current.src = url;
          safePlay(videoRef.current);
        }
      });
    } else {
      video.src = url;
      if (seekPos > 0) {
        const seekOnReady = () => {
          video.currentTime = seekPos;
          pendingSeekRef.current = 0;
          video.removeEventListener('canplay', seekOnReady);
        };
        video.addEventListener('canplay', seekOnReady);
      }
      safePlay(video);
    }
  }, [addToHistory, resolvedTmdbId, entry.tmdbId, entry.title, entry.posterUrl, entry.backdropUrl, entry.seriesStreamId, entry.isKids, setProgress, t]);

  const handleActivateEpisode = useCallback((ep: XtreamEpisode) => {
    consecutiveEpsRef.current = 0; // manual start resets auto-play chain
    const prog = getProgress(ep.id, 'series_episode');
    const pct = prog ? (prog.position / prog.duration) * 100 : 0;
    if (pct > 3 && pct < 95) {
      setPendingEp(ep);
      setPendingSeekPos(prog!.position);
      setResumeModalFocusedBtn(1);
      setShowResumeModal(true);
    } else {
      doPlayEpisode(ep, 0);
    }
  }, [getProgress, doPlayEpisode]);

  // ── Handle fullscreen exit ────────────────────────────────────────────────
  useEffect(() => {
    const onFsChange = () => {
      const active = !!(
        document.fullscreenElement ||
        (document as any).webkitFullscreenElement ||
        (document as any).mozFullScreenElement
      );
      setIsEpisodeFullscreen(active);
      if (!active && playerWrapRef.current) {
        avPlayRef.current.close();
        setIsAvPlayActive(false);
        playerWrapRef.current.style.display = 'none';
        // Reset auto-select so it re-runs with updated progress after watching
        autoSelectedRef.current = false;
        // Clear next-ep overlay timers if fullscreen exits externally
        if (idleTimerRef.current) { clearTimeout(idleTimerRef.current); idleTimerRef.current = null; }
        setShowNextEpOverlay(false);
        consecutiveEpsRef.current = 0;
        hlsRef.current?.destroy();
        hlsRef.current = null;
        const video = videoRef.current;
        if (video) {
          video.pause();
          video.src = '';
        }
        playingEpisodeIdRef.current = null;
        setEpVideoStatus(null);
        setAudioTracks([]);
        setActiveAudioId(0);
        setSubtitleTracks([]);
        setActiveSubtitleId(-1);
      }
    };
    document.addEventListener('fullscreenchange', onFsChange);
    document.addEventListener('webkitfullscreenchange', onFsChange);
    document.addEventListener('mozfullscreenchange', onFsChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFsChange);
      document.removeEventListener('webkitfullscreenchange', onFsChange);
      document.removeEventListener('mozfullscreenchange', onFsChange);
    };
  }, []);

  // ── Save progress on timeupdate (throttled to every 5 s) ─────────────────
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const onTimeUpdate = () => {
      const epId = playingEpisodeIdRef.current;
      if (!epId) return;
      const now = Date.now();
      if (now - lastProgressSaveRef.current < 5000) return;
      lastProgressSaveRef.current = now;
      if (video.duration > 10) {
        setProgress(epId, video.currentTime, video.duration, 'series_episode');
      }
    };
    const onPlaying = () => setEpVideoStatus(null);
    const onWaiting = () => setEpVideoStatus('buffering');
    const onCanPlay = () => setEpVideoStatus(null);
    const onError   = () => setEpVideoStatus('error');
    const onEnded   = () => onEndedRef.current();
    const onLicenseVerificationBlocked = () => {
      const episodeId = playingEpisodeIdRef.current;
      if (episodeId && video.duration > 10) {
        setProgress(episodeId, video.currentTime, video.duration, 'series_episode');
        flushProgress();
      }
    };

    video.addEventListener('timeupdate', onTimeUpdate);
    video.addEventListener('playing',    onPlaying);
    video.addEventListener('waiting',    onWaiting);
    video.addEventListener('canplay',    onCanPlay);
    video.addEventListener('error',      onError);
    video.addEventListener('ended',      onEnded);
    window.addEventListener('auroraflux:license-verification-blocked', onLicenseVerificationBlocked);
    return () => {
      video.removeEventListener('timeupdate', onTimeUpdate);
      video.removeEventListener('playing',    onPlaying);
      video.removeEventListener('waiting',    onWaiting);
      video.removeEventListener('canplay',    onCanPlay);
      video.removeEventListener('error',      onError);
      video.removeEventListener('ended',      onEnded);
      window.removeEventListener('auroraflux:license-verification-blocked', onLicenseVerificationBlocked);
      // Flush progress if user watched something (entry exists locally)
      const epId = playingEpisodeIdRef.current;
      if (epId && getProgress(epId, 'series_episode')) {
        flushProgress();
      }
    };
  }, [setProgress, getProgress, flushProgress]);

  // ── Keep refs in sync with state for use in stable closures ──────────────
  useEffect(() => { xtreamInfoRef.current = xtreamInfo; }, [xtreamInfo]);
  useEffect(() => { seasonsRef.current = seasons; }, [seasons]);

  // ── Clear idle timer ─────────────────────────────────────────────────────
  const clearOverlayTimers = useCallback(() => {
    if (idleTimerRef.current) { clearTimeout(idleTimerRef.current); idleTimerRef.current = null; }
  }, []);

  // ── Show "Continuar assistindo?" overlay (inside fullscreen) ─────────────
  const showNextEpPrompt = useCallback((next: XtreamEpisode) => {
    setNextEpForOverlay(next);
    setNextEpFocusedBtn(0);
    setShowNextEpOverlay(true);
    // 5 min idle guard — if nobody interacts, exit to SeriesDetails
    idleTimerRef.current = setTimeout(() => {
      idleTimerRef.current = null;
      setShowNextEpOverlay(false);
      consecutiveEpsRef.current = 0;
      document.exitFullscreen?.() ?? (document as any).webkitExitFullscreen?.();
    }, 5 * 60 * 1000);
  }, []);

  const handleNextEpYes = useCallback(() => {
    if (!nextEpForOverlay) return;
    clearOverlayTimers();
    setShowNextEpOverlay(false);
    doPlayEpisode(nextEpForOverlay, 0);
  }, [nextEpForOverlay, clearOverlayTimers, doPlayEpisode]);

  const handleNextEpNo = useCallback(() => {
    clearOverlayTimers();
    setShowNextEpOverlay(false);
    consecutiveEpsRef.current = 0;
    document.exitFullscreen?.() ?? (document as any).webkitExitFullscreen?.();
  }, [clearOverlayTimers]);

  // ── Episode ended: save 100% + decide next action ─────────────────────────
  useEffect(() => {
    onEndedRef.current = () => {
      const epId = playingEpisodeIdRef.current;
      if (!epId) return;
      // Mark episode as fully watched
      const duration = isAvPlayActive ? avPlayRef.current.duration() : (videoRef.current?.duration || 0);
      if (duration > 10) {
        setProgress(epId, duration, duration, 'series_episode');
        flushProgress(); // episode finished — flush immediately
      }
      // Find next episode
      const next = findNextEpisode(epId, xtreamInfoRef.current, seasonsRef.current);
      if (!next) {
        // End of series — just close fullscreen
        consecutiveEpsRef.current = 0;
        document.exitFullscreen?.() ?? (document as any).webkitExitFullscreen?.();
        return;
      }
      consecutiveEpsRef.current += 1;
      if (consecutiveEpsRef.current >= 2) {
        // Already watched 2+ in a row — ask before continuing
        showNextEpPrompt(next);
      } else {
        // First or second episode — auto-play silently
        doPlayEpisode(next, 0);
      }
    };
  }, [isAvPlayActive, setProgress, flushProgress, showNextEpPrompt, doPlayEpisode]);

  // ── Auto-select: foca temporada+episódio do último assistido (por updatedAt) ───
  useEffect(() => {
    if (!xtreamInfo || seasons.length === 0 || autoSelectedRef.current) return;

    // Encontra o episódio com maior updatedAt em todas as temporadas
    let bestSeasonIdx = -1;
    let bestEpIdx = 0;
    let bestUpdatedAt = -1;

    for (let i = 0; i < seasons.length; i++) {
      const eps = xtreamInfo.episodes[seasons[i]] ?? [];
      for (let j = 0; j < eps.length; j++) {
        const prog = getProgress(eps[j].id, 'series_episode');
        if (!prog || (prog.updatedAt ?? 0) <= bestUpdatedAt) continue;
        const pct = prog.duration > 0 ? (prog.position / prog.duration) * 100 : 0;
        if (pct < 3) continue; // nunca realmente assistiu
        bestUpdatedAt = prog.updatedAt ?? 0;
        bestSeasonIdx = i;
        if (pct >= 90) {
          // Episódio concluído — apontar para o próximo
          if (j + 1 < eps.length) {
            bestEpIdx = j + 1;
          } else {
            // Último ep da temporada — próxima temporada ep 0
            const nextSeasonIdx = i + 1;
            if (nextSeasonIdx < seasons.length) {
              bestSeasonIdx = nextSeasonIdx;
              bestEpIdx = 0;
            } else {
              // Série toda concluída — fica no último ep
              bestEpIdx = j;
            }
          }
        } else {
          bestEpIdx = j;
        }
      }
    }

    if (bestSeasonIdx === -1) {
      // Nenhum histórico encontrado ainda (getProgress pode estar stale).
      // NÃO marcar autoSelectedRef = true — vai re-rodar quando getProgress atualizar.
      return;
    }

    // Histórico encontrado — travar para não rodar de novo ao navegar.
    autoSelectedRef.current = true;
    setFocusedSeasonIdx(bestSeasonIdx);
    setFocusedEpisodeIdx(bestEpIdx);
  }, [xtreamInfo, seasons, getProgress]); // getProgress como dep para re-rodar após sync

  // ── Quando a temporada muda manualmente, move foco para o ep atual da temporada ──
  // (só roda após o auto-select ter disparado)
  useEffect(() => {
    if (!autoSelectedRef.current) return; // aguarda auto-select
    if (episodes.length === 0) { setFocusedEpisodeIdx(0); return; }
    setFocusedEpisodeIdx(currentEpisodeIdx);
  }, [selectedSeason]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Scroll active season chip into view (scrollLeft — não propaga para o viewport) ──
  useEffect(() => {
    const wrap = seasonWrapRef.current;
    const row  = seasonRowRef.current;
    if (!wrap || !row) return;
    const chip = row.children[focusedSeasonIdx] as HTMLElement | undefined;
    if (!chip) return;
    const target = chip.offsetLeft - wrap.offsetWidth / 2 + chip.offsetWidth / 2;
    wrap.scrollLeft = Math.max(0, target);
  }, [focusedSeasonIdx]);

  // ── Scroll focused rec card into view via scrollLeft ────────────────────
  useEffect(() => {
    const row = recRowRef.current;
    if (!row) return;
    const card = row.children[focusedRec] as HTMLElement | undefined;
    if (!card) return;
    const cardLeft  = card.offsetLeft;
    const cardRight = cardLeft + card.offsetWidth;
    if (cardLeft < row.scrollLeft + 4) {
      row.scrollLeft = Math.max(0, cardLeft - 4);
    } else if (cardRight > row.scrollLeft + row.clientWidth - 4) {
      row.scrollLeft = cardRight - row.clientWidth + 4;
    }
  }, [focusedRec]);

  // ── Scroll focused episode into view ─────────────────────────────────────
  useEffect(() => {
    const list = epListRef.current;
    if (!list) return;
    const row = list.children[focusedEpisodeIdx] as HTMLElement | undefined;
    if (!row) return;
    // Use scrollTop — não propaga para o viewport
    const rowTop    = row.offsetTop;
    const rowBottom = rowTop + row.offsetHeight;
    if (rowTop < list.scrollTop) {
      list.scrollTop = rowTop - 8;
    } else if (rowBottom > list.scrollTop + list.clientHeight) {
      list.scrollTop = rowBottom - list.clientHeight + 8;
    }
  }, [focusedEpisodeIdx]);

  // ── Reset state on entry change ───────────────────────────────────────────
  useEffect(() => {
    setDetails(null);
    setXtreamInfo(null);
    setXtreamInfoLoaded(false);
    setResolvedTmdbId(entry.tmdbId ?? 0);
    setLoadingDetails(true);
    setBackdropUrl(entry.backdropUrl ?? null);
    setFocusSection('buttons');
    setFocusedBtnIdx(0);
    setFocusedSeasonIdx(0);
    setFocusedEpisodeIdx(0);
    setFocusedRec(0);
    setSimilar([]);
    setLoadingSimilar(true);
    autoSelectedRef.current = false;
  }, [entry.seriesStreamId]);

  // ── Fetch Xtream series info ──────────────────────────────────────────────
  useEffect(() => {
    if (!entry.seriesStreamId) {
      setXtreamInfoLoaded(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const info = await getCachedSeriesInfo(String(entry.seriesStreamId));
        if (cancelled) return;
        setXtreamInfo(info);
        if (info.info?.cover && !entry.backdropUrl) {
          setBackdropUrl(info.info.cover);
        }
      } catch {
        // fall through to TMDB
      } finally {
        if (!cancelled) setXtreamInfoLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry.seriesStreamId, entry.tmdbId, entry.backdropUrl]);

  // ── Resolve trusted identity + fetch TMDB details ─────────────────────────
  useEffect(() => {
    if (!xtreamInfoLoaded) return;
    if (contentDataSource === 'xtream') {
      setLoadingDetails(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const rawXtreamTmdbId = xtreamInfo?.info?.tmdb_id;
        const identity = await resolveContentIdentity({
          type: 'series',
          streamId: entry.seriesStreamId,
          entryTmdbId: entry.tmdbId,
          xtreamTmdbId: rawXtreamTmdbId ? Number(rawXtreamTmdbId) : undefined,
          title: xtreamInfo?.info?.name || entry.title || '',
          year: xtreamInfo?.info?.year || xtreamInfo?.info?.releaseDate ||
            xtreamInfo?.info?.release_date || xtreamInfo?.info?.releasedate ||
            entry.year || entry.releaseDate || entry.release_date,
          plot: xtreamInfo?.info?.plot || entry.plot,
        });
        if (cancelled) return;
        setResolvedTmdbId(identity.tmdbId);
        if (identity.tmdbId && identity.tmdbId !== entry.tmdbId) {
          onIdentityResolved?.(identity.tmdbId);
        }
        const det = identity.details as TmdbTvDetailsResult | null;
        setDetails(det);
        if (det?.backdropUrl) setBackdropUrl(det.backdropUrl);
      } catch {
        if (!cancelled) setResolvedTmdbId(0);
      } finally {
        if (!cancelled) setLoadingDetails(false);
      }
    })();
    return () => { cancelled = true; };
  }, [contentDataSource, entry.plot, entry.releaseDate, entry.release_date, entry.seriesStreamId, entry.title, entry.tmdbId, entry.year, onIdentityResolved, xtreamInfo, xtreamInfoLoaded]);

  // ── Fetch similar series ──────────────────────────────────────────────────
  useEffect(() => {
    if (contentDataSource === 'xtream' || entry.isKids) {
      setLoadingSimilar(false);
      return;
    }
    if (loadingDetails) return;
    if (!resolvedTmdbId) {
      setLoadingSimilar(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const results = await getCatalogRecommendations(deviceId, activationPassword, {
          type: 'series',
          tmdbId: resolvedTmdbId,
          title: details?.title || entry.title,
          year: (details?.firstAirDate || entry.releaseDate || entry.release_date || entry.year || '').slice(0, 4),
          plot: details?.overview || entry.plot,
          language: getRuntimeTmdbLanguage(),
        }, consumeCatalogForceRefresh('recommendations-series'));
        if (cancelled) return;
        setSimilar(results.map((item) => ({
          tmdb: {
            id: item.tmdbId,
            title: item.title,
            overview: '',
            posterUrl: item.posterUrl,
            backdropUrl: item.backdropUrl,
            firstAirDate: '',
            voteAverage: 0,
          },
          seriesStreamId: item.streamId,
        })));
      } catch { /* silently degrade */ } finally {
        if (!cancelled) setLoadingSimilar(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activationPassword, contentDataSource, details, deviceId, entry.isKids, entry.plot, entry.releaseDate, entry.release_date, entry.seriesStreamId, entry.title, entry.year, loadingDetails, resolvedTmdbId]);

  // ── Play helper ───────────────────────────────────────────────────────────
  const handlePlayEpisode = useCallback((ep?: XtreamEpisode) => {
    const target = ep ?? episodes[currentEpisodeIdx] ?? episodes[0];
    if (target) handleActivateEpisode(target);
  }, [episodes, currentEpisodeIdx, handleActivateEpisode]);

  // ── Keyboard navigation ───────────────────────────────────────────────────
  useEffect(() => {
    if (!isActive) return;
    const trailerKey = details?.trailerKey ?? xtreamInfo?.info?.youtube_trailer;
    const hasTrailer = Boolean(trailerKey);
    const trailerBtnIdx = hasTrailer ? 1 : -1;
    const favBtnIdx = hasTrailer ? 2 : 1;
    const altsBtnIdx = entry.seriesStreamId ? (hasTrailer ? 3 : 2) : -1;
    const BTN_COUNT = (hasTrailer ? 1 : 0) + (entry.seriesStreamId ? 1 : 0) + 2; // play + [trailer] + fav + [alts]
    const seasonCount = seasons.length;
    const epCount = episodes.length;
    const recCount = similar.length || 8;

    const onKeyDown = (e: KeyboardEvent) => {
      const key = e.key;
      const code = (e as any).keyCode || (e as any).which || 0;
      const isLeft = key === 'ArrowLeft' || key === 'Left' || code === 37;
      const isRight = key === 'ArrowRight' || key === 'Right' || code === 39;
      const isUp = key === 'ArrowUp' || key === 'Up' || code === 38;
      const isDown = key === 'ArrowDown' || key === 'Down' || code === 40;
      const isEnter = key === 'Enter' || key === ' ' || key === 'OK' || code === 13 || code === 32;
      const isBack = key === 'Escape' || key === 'GoBack' || key === 'BrowserBack' || code === 27 || code === 10009 || code === 461;

      // ── Next-episode overlay takes full control, including fullscreen TV keys
      if (showNextEpOverlay) {
        e.preventDefault();
        e.stopImmediatePropagation();
        if (isLeft || isUp) {
          setNextEpFocusedBtn(0);
        } else if (isRight || isDown) {
          setNextEpFocusedBtn(1);
        } else if (isEnter) {
          if (nextEpFocusedBtn === 0) handleNextEpYes();
          else handleNextEpNo();
        } else if (isBack) {
          handleNextEpNo();
        }
        return;
      }

      // When an episode is playing fullscreen, VideoControlsOverlay handles all keys
      if (isEpisodeFullscreen) return;

      // AlternativesModal handles its own keys
      if (showAlternativesModal) return;

      if (showResumeModal) {
        e.preventDefault();
        if (isBack) {
          setShowResumeModal(false);
        } else if (isLeft || isUp) {
          setResumeModalFocusedBtn(0);
        } else if (isRight || isDown) {
          setResumeModalFocusedBtn(1);
        } else if (isEnter && pendingEp) {
          if (resumeModalFocusedBtn === 0) {
            clearProgress(pendingEp.id, 'series_episode');
            doPlayEpisode(pendingEp, 0);
          } else {
            doPlayEpisode(pendingEp, pendingSeekPos);
          }
        }
        return;
      }

      switch (e.key) {
        case 'Escape':
        case 'GoBack':
        case 'BrowserBack':
          e.preventDefault();
          onBack?.();
          break;

        case 'ArrowRight':
          e.preventDefault();
          if (focusSection === 'buttons') {
            if (focusedBtnIdx < BTN_COUNT - 1) {
              setFocusedBtnIdx((p) => p + 1);
            } else {
              // At last button (Favorito) → jump into episode panel
              if (seasonCount > 0) setFocusSection('seasons');
              else if (epCount > 0) setFocusSection('episodes');
            }
          } else if (focusSection === 'seasons') {
            setFocusedSeasonIdx((p) => Math.min(p + 1, seasonCount - 1));
          } else if (focusSection === 'recs') {
            const next = focusedRec + 1;
            if (next < recCount) {
              setFocusedRec(next);
            } else if (epCount > 0) {
              // At last rec card — jump to episode panel
              setFocusSection('episodes');
            }
          }
          // episodes section: ArrowRight does nothing (already rightmost panel)
          break;

        case 'ArrowLeft':
          e.preventDefault();
          if (focusSection === 'buttons') {
            setFocusedBtnIdx((p) => Math.max(p - 1, 0));
          } else if (focusSection === 'seasons') {
            setFocusedSeasonIdx((p) => Math.max(p - 1, 0));
          } else if (focusSection === 'recs') {
            setFocusedRec((p) => Math.max(p - 1, 0));
          } else if (focusSection === 'episodes') {
            // Jump to recs (left panel) — only in tmdb mode
            if (contentDataSource !== 'xtream' && similar.length > 0) {
              setFocusSection('recs');
              setFocusedRec(0);
            }
          }
          break;

        case 'ArrowDown':
          e.preventDefault();
          if (focusSection === 'buttons') {
            // Down from any action button → você também pode gostar (only in tmdb mode)
            if (contentDataSource !== 'xtream' && similar.length > 0) { setFocusSection('recs'); setFocusedRec(0); }
          } else if (focusSection === 'seasons') {
            if (epCount > 0) setFocusSection('episodes');
          } else if (focusSection === 'episodes') {
            if (focusedEpisodeIdx < epCount - 1) {
              setFocusedEpisodeIdx((p) => p + 1);
            }
          }
          // recs: no-op
          break;

        case 'ArrowUp':
          e.preventDefault();
          if (focusSection === 'seasons') {
            setFocusSection('buttons');
          } else if (focusSection === 'episodes') {
            if (focusedEpisodeIdx > 0) {
              setFocusedEpisodeIdx((p) => p - 1);
            } else {
              setFocusSection('seasons');
            }
          } else if (focusSection === 'recs') {
            setFocusSection('buttons');
            setFocusedRec(0);
          }
          break;

        case 'Enter':
        case ' ':
          e.preventDefault();
          if (focusSection === 'buttons') {
            if (focusedBtnIdx === 0) {
              handlePlayEpisode();
            } else if (focusedBtnIdx === trailerBtnIdx) {
              setShowTrailerModal(true);
            } else if (focusedBtnIdx === favBtnIdx) {
              onToggleFavorite?.(resolvedTmdbId);
            } else if (focusedBtnIdx === altsBtnIdx) {
              setShowAlternativesModal(true);
            }
          } else if (focusSection === 'episodes') {
            handlePlayEpisode(episodes[focusedEpisodeIdx]);
          } else if (focusSection === 'recs') {
            const rec = similar[focusedRec];
            if (rec) {
              onSelectRecommended?.({
                tmdbId: rec.tmdb.id,
                title: rec.tmdb.title,
                posterUrl: rec.tmdb.posterUrl ?? undefined,
                backdropUrl: rec.tmdb.backdropUrl ?? undefined,
                seriesStreamId: rec.seriesStreamId,
                plot: rec.tmdb.overview,
                releaseDate: rec.tmdb.firstAirDate,
              });
            }
          }
          break;

        default:
          break;
      }
    };

    window.addEventListener('keydown', onKeyDown, showNextEpOverlay);
    return () => window.removeEventListener('keydown', onKeyDown, showNextEpOverlay);
  }, [
    isActive, isEpisodeFullscreen, focusSection, focusedBtnIdx, focusedSeasonIdx,
    focusedEpisodeIdx, focusedRec,
    seasons, episodes, similar, entry,
    onBack, handlePlayEpisode, onToggleFavorite, onSelectRecommended,
    showResumeModal, resumeModalFocusedBtn, pendingEp, pendingSeekPos,
    clearProgress, doPlayEpisode, details, xtreamInfo,
    showNextEpOverlay, nextEpFocusedBtn, handleNextEpYes, handleNextEpNo,
    contentDataSource, showAlternativesModal,
  ]);

  // ── Derived display values ────────────────────────────────────────────────
  const title = entry.title ?? details?.title ?? xtreamInfo?.info?.name ?? '…';
  const rating = details
    ? details.voteAverage.toFixed(1)
    : (xtreamInfo?.info?.rating ? parseFloat(String(xtreamInfo.info.rating)).toFixed(1) : '—');
  const metaLine = details
    ? [
        details.countries[0] ?? '',
        formatDate(details.firstAirDate),
        details.originalTitle !== details.title ? details.originalTitle : '',
      ]
        .filter(Boolean)
        .join(' | ')
    : '';
  const genres   = details?.genres.join(', ') ?? '';
  const creators = details?.creators.join(', ') ?? (xtreamInfo?.info?.director ?? '');
  const cast     = details?.cast.join(', ') ?? (xtreamInfo?.info?.cast ?? '');
  const overview = details?.overview ?? (xtreamInfo?.info?.plot ?? '');

  const backdropStyle: React.CSSProperties = backdropUrl
    ? { backgroundImage: `url(${backdropUrl})` }
    : {};

  const epNumDigits = episodes.length > 999 ? 4 : episodes.length > 99 ? 3 : 2;
  const infoSeasonCount = seasons.length || details?.numberOfSeasons || 0;
  const loadingPlaceholders = Array.from({ length: 8 });

  // Season label in episode panel header
  const seasonNum = selectedSeason
    ? String(parseInt(selectedSeason, 10)).padStart(2, '0')
    : '';
  const seasonLabel = selectedSeason
    ? t(
        episodes.length === 1 ? 'seriesDetails.seasonLabel' : 'seriesDetails.seasonLabelPlural',
        { num: seasonNum, count: episodes.length },
      )
    : '';

  return (
    <>
      <div className="series-details">
        <div className="series-details__backdrop" style={backdropStyle} />
        {/* ── Cover (left column) ─────────────────────────────────── */}
        <div className="series-details__cover">
          {(details?.posterUrl || entry.posterUrl || xtreamInfo?.info?.cover) ? (
            <img className="series-details__cover-img" src={details?.posterUrl || entry.posterUrl || xtreamInfo?.info?.cover} alt={title} />
          ) : backdropUrl ? (
            <div className="series-details__cover-bg" style={{ backgroundImage: `url(${backdropUrl})` }} />
          ) : (
            <div className="series-details__cover-placeholder">
              <div className="series-details__cover-icon"><FiPlay size={28} /></div>
            </div>
          )}
        </div>

        {/* ── Info column (centre) ─────────────────────────────────── */}
        <div className="series-details__info">
          {loadingDetails ? (
            <div className="series-details__info-skeleton">
              <div className="series-details__title-row">
                <span className="series-details__title">{entry.title || xtreamInfo?.info?.name || '…'}</span>
              </div>
              <div className="series-details__skel series-details__skel--meta" />
              <div className="series-details__skel series-details__skel--meta" />
              <div className="series-details__skel series-details__skel--meta" />
              <div className="series-details__skel series-details__skel--paragraph" />
              <div className="series-details__skel series-details__skel--paragraph series-details__skel--paragraph-short" />
            </div>
          ) : (
            <>
              <div className="series-details__title-row">
                <span className="series-details__title">{title}</span>
                <span className="series-details__rating">{rating}</span>
              </div>
              {metaLine   && <p className="series-details__meta">{metaLine}</p>}
              {genres     && <p className="series-details__meta"><strong>{t('seriesDetails.genre')}</strong>{genres}</p>}
              {creators   && <p className="series-details__meta"><strong>{t('seriesDetails.creator')}</strong>{creators}</p>}
              {cast       && <p className="series-details__meta"><strong>{t('seriesDetails.cast')}</strong>{cast}</p>}
              {infoSeasonCount > 0 && (
                <p className="series-details__meta">
                  <strong>{t('seriesDetails.seasons')}</strong>{infoSeasonCount}
                  {totalEpisodes > 0 && (
                    <>&nbsp;·&nbsp;<strong>{t('seriesDetails.episodes')}</strong>{totalEpisodes}</>
                  )}
                </p>
              )}
              {overview   && <p className="series-details__synopsis"><strong>{t('seriesDetails.synopsis')}</strong>{overview}</p>}
            </>
          )}
        </div>

        {/* ── Action buttons ───────────────────────────────────────── */}
        {(() => {
          const trailerKey = details?.trailerKey ?? xtreamInfo?.info?.youtube_trailer;
          const hasTrailer = Boolean(trailerKey);
          const favBtnIdx = hasTrailer ? 2 : 1;
          const altsBtnIdx = entry.seriesStreamId ? (hasTrailer ? 3 : 2) : -1;
          return (
            <div className="series-details__actions">
              <button
                className={[
                  'series-details__action-btn',
                  focusSection === 'buttons' && focusedBtnIdx === 0 ? 'series-details__action-btn--focused' : '',
                ].filter(Boolean).join(' ')}
                disabled={!entry.seriesStreamId}
                onClick={() => handlePlayEpisode()}
              >
                <FiMaximize2 size={16} />
                {t('seriesDetails.watch')}
              </button>

              {hasTrailer && (
                <button
                  className={[
                    'series-details__action-btn',
                    focusSection === 'buttons' && focusedBtnIdx === 1 ? 'series-details__action-btn--focused' : '',
                  ].filter(Boolean).join(' ')}
                  onClick={() => setShowTrailerModal(true)}
                >
                  <FiPlay size={16} />
                  {t('seriesDetails.trailer')}
                </button>
              )}

              <button
                className={[
                  'series-details__action-btn',
                  isFavorite ? 'series-details__action-btn--fav-active' : '',
                  focusSection === 'buttons' && focusedBtnIdx === favBtnIdx ? 'series-details__action-btn--focused' : '',
                ].filter(Boolean).join(' ')}
                onClick={() => onToggleFavorite?.(resolvedTmdbId)}
              >
                <FiHeart size={16} />
                {t('seriesDetails.favorite')}
              </button>

              {entry.seriesStreamId && (
                <button
                  className={[
                    'series-details__action-btn',
                    focusSection === 'buttons' && focusedBtnIdx === altsBtnIdx ? 'series-details__action-btn--focused' : '',
                  ].filter(Boolean).join(' ')}
                  onClick={() => setShowAlternativesModal(true)}
                >
                  <FiFilm size={16} />
                  {t('alternatives.button')}
                </button>
              )}
            </div>
          );
        })()}

        {/* ── Recommended series (centre-bottom) ───────────────────── */}
        {contentDataSource !== 'xtream' && !entry.isKids && (loadingSimilar || similar.length > 0) && (
        <div className="series-details__recs-section" ref={recsRef}>
          <p className="series-details__section-title">{t('seriesDetails.recommended')}</p>
          <div className="series-details__recs-clip">
            <div className="series-details__recs-row" ref={recRowRef}>
              {loadingSimilar
                ? loadingPlaceholders.map((_, i) => (
                    <RecCard key={i} loading focused={false} onSelect={() => {}} />
                  ))
                : similar.map((rec, i) => (
                    <RecCard
                      key={rec.tmdb.id}
                      series={rec.tmdb}
                      focused={focusSection === 'recs' && focusedRec === i}
                      onSelect={() =>
                        onSelectRecommended?.({
                          tmdbId: rec.tmdb.id,
                          title: rec.tmdb.title,
                          posterUrl: rec.tmdb.posterUrl ?? undefined,
                          backdropUrl: rec.tmdb.backdropUrl ?? undefined,
                          seriesStreamId: rec.seriesStreamId,
                          plot: rec.tmdb.overview,
                          releaseDate: rec.tmdb.firstAirDate,
                        })
                      }
                    />
                  ))}
            </div>
          </div>
        </div>
        )}

        {/* ── Episode panel (right column) ─────────────────────────── */}
        {seasons.length > 0 && (
          <div className="series-details__ep-panel">
            {/* Season chips — single scrollable row */}
            <div className="series-details__seasons-wrap" ref={seasonWrapRef}>
              <div className="series-details__seasons" ref={seasonRowRef}>
                {seasons.map((s, i) => (
                  <SeasonChip
                    key={s}
                    seasonKey={s}
                    status={getSeasonStatus(xtreamInfo?.episodes[s] ?? [], effectiveProgress?.episodes)}
                    active={i === focusedSeasonIdx}
                    focused={focusSection === 'seasons' && i === focusedSeasonIdx}
                    onClick={() => { setFocusedSeasonIdx(i); setFocusSection('seasons'); }}
                  />
                ))}
              </div>
            </div>

            {/* Season / episode count label */}
            {seasonLabel && (
              <p className="series-details__ep-label">{seasonLabel}</p>
            )}

            {/* Vertical episode list */}
            <div className="series-details__ep-list" ref={epListRef}>
              {episodes.map((ep, i) => {
                const seasonNum = selectedSeason ? parseInt(selectedSeason, 10) : NaN;
                const tmdbEp = (!isNaN(seasonNum) && tmdbSeasonCache.has(seasonNum))
                  ? tmdbSeasonCache.get(seasonNum)!.get(Number(ep.episode_num))
                  : undefined;
                return (
                  <EpisodeRow
                    key={ep.id}
                    episode={ep}
                    tmdbEp={tmdbEp}
                    seasonKey={selectedSeason ?? '1'}
                    progress={effectiveProgress?.episodes[ep.id]}
                    numDigits={epNumDigits}
                    focused={focusSection === 'episodes' && i === focusedEpisodeIdx}
                    current={i === currentEpisodeIdx}
                    onClick={() => {
                      setFocusedEpisodeIdx(i);
                      setFocusSection('episodes');
                      handleActivateEpisode(ep);
                    }}
                  />
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* ── Hidden fullscreen episode player ──────────────────────── */}
      <div
        ref={playerWrapRef}
        style={{
          display: 'none',
          position: 'fixed',
          top: 0,
          left: 0,
          width: 1280,
          height: 720,
          zIndex: 9999,
          background: '#000',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <video
          ref={videoRef}
          style={{ display: isAvPlayActive ? 'none' : 'block', width: '100%', height: '100%', objectFit: 'contain' }}
          playsInline
          autoPlay
        />
        <object
          type="application/avplayer"
          aria-label="Samsung AVPlay"
          style={{ display: isAvPlayActive ? 'block' : 'none', position: 'absolute', inset: 0, width: '100%', height: '100%' }}
        />
        {epVideoStatus && <PlaybackStatusOverlay status={epVideoStatus} />}
        <VideoControlsOverlay
          videoRef={videoRef}
          title={entry.title ?? ''}
          subtitle={fsEpTitle}
          blockInput={showNextEpOverlay}
          externalPlayback={isAvPlayActive ? avPlayRef.current : undefined}
        />
        {showNextEpOverlay && nextEpForOverlay && (
          <div className="series-details__next-ep-overlay">
            <p className="series-details__next-ep-eyebrow">{t('seriesDetails.nextEpisode')}</p>
            <p className="series-details__next-ep-name">
              {t('seriesDetails.seasonPrefix')}{nextEpForOverlay.season}&nbsp;{t('seriesDetails.episodePrefix')}{nextEpForOverlay.episode_num}
              {nextEpForOverlay.title ? ` · ${nextEpForOverlay.title}` : ''}
            </p>
            <div className="series-details__next-ep-btns">
              <button
                className={[
                  'series-details__next-ep-btn',
                  'series-details__next-ep-btn--primary',
                  nextEpFocusedBtn === 0 ? 'series-details__next-ep-btn--focused' : '',
                ].filter(Boolean).join(' ')}
                onClick={handleNextEpYes}
              >
                <FiPlay size={14} />
                {t('seriesDetails.continueBtn')}
              </button>
              <button
                className={[
                  'series-details__next-ep-btn',
                  nextEpFocusedBtn === 1 ? 'series-details__next-ep-btn--focused' : '',
                ].filter(Boolean).join(' ')}
                onClick={handleNextEpNo}
              >
                {t('seriesDetails.stopBtn')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Resume modal ──────────────────────────────────────────── */}
      {showResumeModal && pendingEp && (
        <div className="series-details__resume-overlay">
          <div className="series-details__resume-modal">
            <p className="series-details__resume-title">{t('seriesDetails.resumeTitle')}</p>
            <div className="series-details__resume-btns">
              <button
                className={`series-details__resume-btn${resumeModalFocusedBtn === 0 ? ' series-details__resume-btn--focused' : ''}`}
                onClick={() => { clearProgress(pendingEp.id, 'series_episode'); doPlayEpisode(pendingEp, 0); }}
              >
                <FiPlay size={14} />
                {t('seriesDetails.playFromStart')}
              </button>
              <button
                className={`series-details__resume-btn series-details__resume-btn--primary${resumeModalFocusedBtn === 1 ? ' series-details__resume-btn--focused' : ''}`}
                onClick={() => doPlayEpisode(pendingEp, pendingSeekPos)}
              >
                <FiMaximize2 size={14} />
                {t('seriesDetails.continueFrom')}{formatProgressTime(pendingSeekPos)}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Trailer modal ─────────────────────────────────────────── */}
      {showTrailerModal && (details?.trailerKey ?? xtreamInfo?.info?.youtube_trailer) && (
        <TrailerModal
          trailerKey={(details?.trailerKey ?? xtreamInfo!.info!.youtube_trailer)!}
          onClose={() => setShowTrailerModal(false)}
        />
      )}

      {/* ── Alternatives modal ────────────────────────────────────── */}
      {showAlternativesModal && entry.seriesStreamId && (
        <AlternativesModal
          currentName={entry.title ?? details?.title ?? xtreamInfo?.info?.name ?? ''}
          currentStreamId={entry.seriesStreamId}
          type="series"
          onSelect={(item: XtreamContent) => {
            setShowAlternativesModal(false);
            if (String(item.series_id ?? item.stream_id ?? '') === String(entry.seriesStreamId)) return;
            onSelectRecommended?.({
              tmdbId: 0,
              title: item.name,
              posterUrl: item.stream_icon || item.cover || undefined,
              seriesStreamId: String(item.series_id ?? item.stream_id ?? ''),
              plot: item.plot || undefined,
              year: item.year || undefined,
              releaseDate: item.releaseDate || item.release_date || undefined,
            });
          }}
          onClose={() => setShowAlternativesModal(false)}
        />
      )}

    </>
  );
};

export default SeriesDetails;
